import { Icon20ErrorCircleOutline } from '@vkontakte/icons';
import { Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BillingSelectors } from 'reactApp/modules/billing/billing.selectors';
import { updateBillingDataRequest } from 'reactApp/modules/billingData/billingData.module';
import { type BillingDataSubscriptionInfo, BillingDataSubscriptionInfoStatus } from 'reactApp/modules/billingData/billingData.types';
import { getGraceSubscriptionText } from 'reactApp/modules/billingData/grace.text';
import { CardSelectors } from 'reactApp/modules/creditCard/creditCard.selectors';
import { getLoadingState } from 'reactApp/modules/loading/loading.selectors';
import { openRenewSubscription } from 'reactApp/modules/payment/payment.module';
import {
    ESubscriptionModalAction,
    useSendSubscriptionModalAnalytics,
} from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { isYear2Period } from 'reactApp/utils/Period';

import styles from './GraceSubscription.css';

interface GraceSubscriptionProps {
    isMobile?: boolean;
    product: Product;
    subscription: BillingDataSubscriptionInfo;
}

export function GraceSubscription({ subscription, product, isMobile }: GraceSubscriptionProps) {
    const { status, renewable, autorenewal } = subscription;
    const isRenewable = status === BillingDataSubscriptionInfoStatus.ACTIVE && renewable && !isYear2Period(product?.period);
    const dispatch = useDispatch();
    const { isLoading: isRenewLoading } = useSelector(BillingSelectors.getLoadingState);
    const { card, isLoading: isCardLoading } = useSelector(CardSelectors.getCard);
    const isSubscriptionsLoading = useSelector((state: RootState) => getLoadingState(state, [updateBillingDataRequest.toString()]));
    const sendAnalytics = useSendSubscriptionModalAnalytics(subscription);

    useEffect(() => {
        sendAnalytics(ESubscriptionModalAction.graceProlongShow);
    }, []);

    const handleOnAutoRenewAddClick = useCallback(() => {
        sendAnalytics(ESubscriptionModalAction.graceProlongClick);
        dispatch(
            openRenewSubscription({
                isMobile,
                isGrace: true,
                subscriptionId: subscription.id,
            })
        );
    }, [dispatch, card, sendAnalytics, subscription.id, isMobile]);

    return (
        <div className={classNames(styles.root, { [styles.rootMobile]: isMobile })}>
            <Icon20ErrorCircleOutline />
            <div className={styles.container}>
                <Text className={styles.text}>
                    {getGraceSubscriptionText({
                        grace: subscription.grace,
                        currentPeriod: subscription.currentPeriod,
                        product,
                        isAutoRenewable: autorenewal,
                    })}
                </Text>
                {isRenewable && (
                    <Button
                        data-name="grace-renew"
                        theme="vk"
                        middle
                        onClick={handleOnAutoRenewAddClick}
                        disabled={isRenewLoading || isSubscriptionsLoading || isCardLoading}
                        sizeMode={ButtonSizeMode.small}
                        className={styles.button}
                    >
                        Доплатить
                    </Button>
                )}
            </div>
        </div>
    );
}
